import { z } from 'zod';
/**
 * What a failed field says to the person who filled it in.
 *
 * Zod's defaults are written for whoever wrote the schema: leaving the first
 * name blank on the registration form produced "String must contain at least 1
 * character(s)", which a receptionist has to translate before they can act on
 * it. The same schemas run on the server, so installing this once here fixes
 * both the form and the API response.
 *
 * Only the defaults are replaced. A message written by hand in a schema —
 * "Provide a date of birth or an approximate age" — always wins, because Zod
 * only consults the map when no message was given.
 */
const TYPE_NAMES = {
    string: 'some text',
    number: 'a number',
    boolean: 'yes or no',
    date: 'a date',
    bigint: 'a whole number',
    array: 'a list',
    object: 'a set of details',
};
/** "at least 3 characters" rather than "at least 3 character(s)". */
const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`;
export const friendlyErrorMap = (issue, ctx) => {
    switch (issue.code) {
        case z.ZodIssueCode.invalid_type: {
            if (issue.received === 'undefined' || issue.received === 'null')
                return { message: 'This is required' };
            return { message: `Enter ${TYPE_NAMES[String(issue.expected)] ?? String(issue.expected)}` };
        }
        case z.ZodIssueCode.too_small: {
            const min = Number(issue.minimum);
            if (issue.type === 'string') {
                return { message: min <= 1 ? 'This is required' : `Use at least ${plural(min, 'character')}` };
            }
            if (issue.type === 'array') {
                return { message: min <= 1 ? 'Add at least one' : `Add at least ${min}` };
            }
            if (issue.type === 'date')
                return { message: 'Choose a later date' };
            return { message: issue.inclusive ? `Must be ${min} or more` : `Must be more than ${min}` };
        }
        case z.ZodIssueCode.too_big: {
            const max = Number(issue.maximum);
            if (issue.type === 'string')
                return { message: `Keep it to ${plural(max, 'character')}` };
            if (issue.type === 'array')
                return { message: `No more than ${max}` };
            if (issue.type === 'date')
                return { message: 'Choose an earlier date' };
            return { message: issue.inclusive ? `Must be ${max} or less` : `Must be less than ${max}` };
        }
        case z.ZodIssueCode.invalid_string: {
            if (issue.validation === 'email')
                return { message: 'That does not look like an email address' };
            if (issue.validation === 'url')
                return { message: 'That does not look like a web address' };
            if (issue.validation === 'uuid')
                return { message: 'That is not a valid reference' };
            if (issue.validation === 'datetime')
                return { message: 'That is not a valid date and time' };
            if (issue.validation === 'regex')
                return { message: 'That is not in the expected format' };
            return { message: 'That is not in the expected format' };
        }
        case z.ZodIssueCode.invalid_enum_value:
            return { message: `Choose one of: ${issue.options.join(', ')}` };
        case z.ZodIssueCode.unrecognized_keys:
            return { message: `Not expected here: ${issue.keys.join(', ')}` };
        case z.ZodIssueCode.invalid_date:
            return { message: 'That is not a valid date' };
        case z.ZodIssueCode.not_multiple_of:
            return { message: `Must be a multiple of ${issue.multipleOf}` };
        default:
            return { message: ctx.defaultError };
    }
};
/** Call once, as early as possible, on both the server and the client. */
export function installFriendlyValidationMessages() {
    z.setErrorMap(friendlyErrorMap);
}
//# sourceMappingURL=validation-messages.js.map